import React, { createContext, useCallback, useContext, useRef, useState } from "react";

const MediaStreamContext = createContext(null);

export function MediaStreamProvider({ children }) {
  const [stream, setStream] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | requesting | granted | denied | unsupported
  const streamRef = useRef(null);

  const requestMediaAccess = useCallback(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("unsupported");
      return Promise.reject(new Error("unsupported"));
    }

    // Already have a live stream — reuse it instead of prompting again.
    if (streamRef.current && streamRef.current.active) {
      setStatus("granted");
      return Promise.resolve(streamRef.current);
    }

    setStatus("requesting");

    return navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((mediaStream) => {
        streamRef.current = mediaStream;
        setStream(mediaStream);
        setStatus("granted");
        return mediaStream;
      })
      .catch((err) => {
        setStatus(err?.name === "NotFoundError" ? "unsupported" : "denied");
        throw err;
      });
  }, []);

  // Call this when the interview is fully over (not when leaving the
  // loading screen) so the camera/mic light turns off.
  const stopMediaAccess = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
    setStatus("idle");
  }, []);

  return (
    <MediaStreamContext.Provider
      value={{ stream, status, requestMediaAccess, stopMediaAccess }}
    >
      {children}
    </MediaStreamContext.Provider>
  );
}

export function useMediaStream() {
  const ctx = useContext(MediaStreamContext);
  if (!ctx) {
    throw new Error("useMediaStream must be used inside a MediaStreamProvider");
  }
  return ctx;
}